/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { accountCategoriesObjectiveMap } from '@components/accountCategories';
import { extractIdFromUrl } from '@cypress/utils/helpers';
import type { AdvertiserEvent } from '@local-types/advertiserEvent';
import type {
  CampaignObjective,
  CampaignObjectiveTypes,
} from '@local-types/campaignObjectiveTypes';
import type { Keys, Nullable } from '@local-types/general';
import type { Advertiser } from '@local-types/advertiser';
import { fields as advancedOptionsFields } from '@components/WizardContainers/CampaignSection/panes/AdvancedCampaignDeliveryOptionsPane/components/AdvancedDeliveryOptionsForm/constants';
import moment, { Moment } from 'moment-timezone';
import { removeNullValues } from '../../../utils';
import { Objective, RoughCampaign } from './types';

import { Stack } from '@mui/material';
import type { Control, FieldValues } from 'react-hook-form';
import { GoalEventPicker } from './components/GoalEventPicker';
import { GoalTypeAndAmount } from './components/GoalTypeAndAmount';
import {
  fields,
  objectives,
  objectiveValues,
  trackingLockedInfo,
} from './constants';

const getIsObjectiveLocked = ({
  objective,
  advertiser,
}: {
  objective: Objective;
  advertiser: Advertiser;
}) =>
  getLockedStatus({
    option: objective,
    disabledObjectiveOptions: {
      objectiveGoalEventSet: advertiser.advertiserevent_set,
    },
  });

export const getInitialObjective = ({
  values,
  advertiser,
}: {
  values: Record<string, any>;
  advertiser: Advertiser;
}) => {
  const recommendedCampaignObjective = (
    Object.values(
      getActualObjectives(advertiser?.campaign_objective_types)
    )[0] as CampaignObjective | undefined
  )?.value;

  const initialObjective = values?.objective ?? recommendedCampaignObjective;

  if (
    initialObjective &&
    !getIsObjectiveLocked({
      objective: Object.values(objectives).find(
        o => o.value === initialObjective
      )!,
      advertiser,
    })
  ) {
    return initialObjective;
  }

  return (
    Object.values(objectives).find(o => {
      return !getIsObjectiveLocked({ objective: o, advertiser });
    })?.value ?? null
  );
};

export const parseApiDataToFormValues = ({
  campaign: values,
  advertiser,
}: {
  campaign: Record<string, any>;
  advertiser: Advertiser;
}) => {
  const {
    id,
    name,
    startDate,
    endDate,
    budget,
    status,
    dailyBudget,
    dailyBudgetCap,
    experimentType,
    experimentPlan,
    freqCaps = [],
    objectiveGoal,
    objectiveGoalEvent,
    objectiveValueCost,
    objectiveValueMultiplier,
    displayPct,
    budgetType,
  } = values || {};

  const initialObjective = getInitialObjective({
    values,
    advertiser,
  });

  return {
    [fields.name.path]: name,
    [fields.advertiserName.path]: advertiser.name,
    [fields.status.path]: status,
    [fields.startDate.path]: startDate
      ? moment(startDate)
      : id
      ? null
      : undefined,
    [fields.startTime.path]: startDate
      ? moment(startDate)
      : id
      ? null
      : undefined,
    [fields.endDate.path]: endDate ? moment(endDate) : id ? null : undefined,
    [fields.endTime.path]: endDate ? moment(endDate) : id ? null : undefined,
    [fields.budgetType.path]: budgetType,
    [fields.lifetimeBudget.path]: budget,
    [fields.dailyBudget.path]: dailyBudget,
    [fields.dailyBudgetCap.path]: dailyBudgetCap,
    [fields.displayPct.path]: Number(displayPct) ? true : undefined,
    [fields.objective.path]: initialObjective,
    [fields.objectiveGoalEvent.path]: objectiveGoalEvent,
    [fields.objectiveGoalType.path]: objectiveGoal,
    [fields.objectiveGoalValueCost.path]: objectiveValueCost,
    [fields.objectiveGoalValueMultiplier.path]: objectiveValueMultiplier,
    [fields.advancedOptions.path]: {
      isIncremental: !!experimentType,
      isExperiment: !!experimentPlan,
      isCapAds: freqCaps.length > 0,
      freqCaps,
      experimentPlan: experimentPlan ?? undefined,
      experimentType: experimentType ?? undefined,
    },
  };
};

export const mustIncludePostFields = ['objective_goal'];
export const mustIncludePatchields = ['objective_goal', 'end_date'];

export const filter =
  (mandatoryFields: string[] = []) =>
  (key: string, value: any) => {
    if (mandatoryFields.includes(key)) {
      return true;
    }
    return value;
  };

export const parseFormValuesToApiData = (
  updatedValues: Record<string, any>,
  formValues: Record<string, any>,
  campaignId?: number,
  options = {
    filter: filter(mustIncludePostFields),
  }
) => {
  // if daily budget, lifetime budget or daily budget cap is in form values, make sure
  // budget type is as well
  if (
    fields.dailyBudget.path in updatedValues ||
    fields.lifetimeBudget.path in updatedValues ||
    fields.dailyBudgetCap.path in updatedValues
  ) {
    updatedValues.budgetType = formValues.budgetType;
  }

  const {
    name,
    advertiser,
    budgetType,
    dailyBudget,
    lifetimeBudget,
    objective,
    objectiveGoalEvent,
    objectiveGoalType,
    objectiveGoalValueCost,
    objectiveGoalValueMultiplier,
    advancedOptions,
    status,
  } = updatedValues || {};

  if (
    fields.endDate.path in updatedValues ||
    fields.endTime.path in updatedValues
  ) {
    updatedValues.endTime = updatedValues.endTime || formValues.endTime;
    updatedValues.endDate = updatedValues.endDate || formValues.endDate;
  }

  if (
    fields.startDate.path in updatedValues ||
    fields.startTime.path in updatedValues
  ) {
    updatedValues.startTime = updatedValues.startTime || formValues.startTime;
    updatedValues.startDate = updatedValues.startDate || formValues.startDate;
  }

  const campaignBudgetType = budgetType || formValues.budgetType;

  const data: Partial<RoughCampaign> = removeNullValues(
    {
      name,
      budget:
        campaignBudgetType === 'lifetime_budget'
          ? lifetimeBudget
          : dailyBudget,
      budget_type: budgetType,
      start_date: combineDateAndTime(
        updatedValues.startDate,
        updatedValues.startTime
      ),
      end_date:
        combineDateAndTime(updatedValues.endDate, updatedValues.endTime) ??
        null,
      advertiser,
      status,
      objective,
      objective_goal_event: objectiveGoalEvent,
      objective_goal: objectiveGoalType,
      objective_value_cost: objectiveGoalValueCost,
      objective_value_multiplier: objectiveGoalValueMultiplier,
    },
    options.filter ?? filter
  );

  // if endDate or endTime have not been updated, don't send a blank end_date
  if (
    !(
      fields.endDate.path in updatedValues ||
      fields.endTime.path in updatedValues
    )
  ) {
    delete data.end_date;
  }

  // setting nullable, empty string fields here
  if (fields.displayPct.path in updatedValues) {
    data.display_pct = String(updatedValues.displayPct ? 0.1 : 0.0);
  }

  if (fields.dailyBudgetCap.path in updatedValues) {
    data.daily_budget_cap =
      campaignBudgetType === 'lifetime_budget'
        ? updatedValues.dailyBudgetCap
        : null;
  }

  // if the campaign exists, include all goals-related fields in the request
  // that has changed in case some of the fields need to be reset to null
  if (campaignId) {
    if (fields.objectiveGoalEvent.path in updatedValues) {
      data.objective_goal_event = updatedValues.objectiveGoalEvent;
    }

    if (fields.objectiveGoalValueCost.path in updatedValues) {
      data.objective_value_cost = updatedValues.objectiveGoalValueCost;
    }

    if (fields.objectiveGoalValueMultiplier.path in updatedValues) {
      data.objective_value_multiplier =
        updatedValues.objectiveGoalValueMultiplier;
    }
  }

  if (fields.advancedOptions.path in updatedValues) {
    const {
      isCapAds,
      isIncremental,
      isExperiment,
      experimentPlan,
      experimentType,
      freqCaps,
    } = advancedOptions || {};

    if (advancedOptionsFields.freqCaps.path in advancedOptions) {
      data.freq_caps = isCapAds ? removeNullValuesFromFreqCaps(freqCaps) : [];
    }

    if (advancedOptionsFields.experimentPlan.path in advancedOptions) {
      data.experiment_plan = isExperiment ? experimentPlan : null;
    }

    if (advancedOptionsFields.experimentType.path in advancedOptions) {
      data.experiment_type = isIncremental ? experimentType : null;
    }
  }

  return data;
};

export const getRecommendedCampaignObjectForAdvertiser = (
  advertiser: Advertiser
) => {
  // if advertiser does not have tracking setup, return audienceReach
  if (
    !advertiser?.advertiserevent_set ||
    advertiser.advertiserevent_set.length === 0
  ) {
    return 'audienceReach';
  }
  if (advertiser?.category) {
    const categoryId = extractIdFromUrl(advertiser.category);
    if (categoryId) {
      return Object.keys(accountCategoriesObjectiveMap).find(key => {
        return accountCategoriesObjectiveMap[
          key as Keys<typeof accountCategoriesObjectiveMap>
        ].includes(parseInt(categoryId, 10));
      });
    }
  }
  return null;
};

const combineDateAndTime = (
  date?: string | Moment,
  time?: string | Moment
) => {
  if (!date || !time) return null;
  const dateMoment = moment(date);
  const timeMoment = moment(time);

  const combinedMoment = moment({
    year: dateMoment.year(),
    month: dateMoment.month(),
    day: dateMoment.date(),
    hour: timeMoment.hour(),
    minute: timeMoment.minute(),
    second: timeMoment.second(),
  });

  return combinedMoment;
};

const removeNullValuesFromFreqCaps = (freqCaps: Record<string, any>[]) => {
  if (!freqCaps) return;

  return freqCaps.map(freqCap => {
    return removeNullValues(freqCap);
  });
};

export const getUnlockedContent = ({
  objective,
  contentsProps,
  objectiveGoalsAllowed,
}: {
  objective: Objective;
  contentsProps: any;
  objectiveGoalsAllowed: boolean;
}) => {
  const { control, setValue, objectiveGoalEventSet } = contentsProps;

  return (
    objectiveGoalsAllowed && (
      <Stack spacing={3} sx={{ marginBottom: 2.5 }}>
        {objective.hasGoalEvent && (
          <GoalEventPicker
            control={control}
            eventSet={objectiveGoalEventSet}
            value={objective.value}
          />
        )}
        {objective.goalTypes?.length > 0 && (
          <GoalTypeAndAmount
            control={control}
            value={objective.value}
            setValue={setValue}
          />
        )}
      </Stack>
    )
  );
};

const isLocked = (disabledObjectiveOptions: {
  objectiveGoalEventSet: AdvertiserEvent[];
}) => {
  const objectiveGoalEventSet =
    disabledObjectiveOptions?.objectiveGoalEventSet;
  return !objectiveGoalEventSet || objectiveGoalEventSet.length === 0;
};

export const getLockedStatus = ({
  option,
  disabledObjectiveOptions,
}: {
  option: Objective;
  disabledObjectiveOptions: {
    objectiveGoalEventSet: AdvertiserEvent[];
  };
}) => {
  if (
    option.hasGoalEvent &&
    !disabledObjectiveOptions?.objectiveGoalEventSet?.length
  ) {
    return true;
  }

  switch (option.value) {
    case objectiveValues.appInstalls:
    case objectiveValues.websiteSales:
    case objectiveValues.websiteVisits:
      return isLocked(disabledObjectiveOptions);
    default:
      return false;
  }
};

export const getLockedInfo = ({
  option,
  lockedInfo,
}: {
  option: Objective;
  lockedInfo: {
    trackingLockedInfo: string;
  };
}) => {
  switch (option.value) {
    case objectiveValues.appInstalls:
    case objectiveValues.websiteSales:
    case objectiveValues.websiteVisits:
      return lockedInfo.trackingLockedInfo;
    default:
      return null;
  }
};

export const getActualObjectives = (advertiserObjectives = {}) =>
  Object.keys(objectives)
    .filter(key => key in advertiserObjectives)
    .reduce((acc, key) => {
      acc[key] = objectives[key];
      return acc;
    }, {} as Record<string, Objective>);

export const getObjectivesOptions = ({
  advertiserObjectives,
  control,
  copies,
  objectiveGoalsAllowed,
  objectiveGoalEventSet,
  recommendedCampaignObjective,
  setValue,
}: {
  advertiserObjectives: CampaignObjectiveTypes;
  control: Control<FieldValues>;
  copies: Record<string, any>;
  objectiveGoalsAllowed: boolean;
  objectiveGoalEventSet: AdvertiserEvent[];
  recommendedCampaignObjective?: Nullable<string>;
  setValue: (name: string, value: any) => void;
}) => {
  objectiveGoalEventSet.forEach(obj => {
    obj.value = obj.id;
  });

  const contentsProps = { control, setValue, objectiveGoalEventSet };

  const actualObjectives = getActualObjectives(advertiserObjectives);

  return Object.keys(actualObjectives).map(key => ({
    ...actualObjectives[key],
    recommended: recommendedCampaignObjective === actualObjectives[key].value,
    recommendedInfo: copies.objectiveRecommendation,
    locked: getLockedStatus({
      option: actualObjectives[key],
      disabledObjectiveOptions: { objectiveGoalEventSet },
    }),
    lockedInfo: getLockedInfo({
      option: actualObjectives[key],
      lockedInfo: { trackingLockedInfo },
    }),
    unlockedContent: getUnlockedContent({
      objective: actualObjectives[key],
      contentsProps,
      objectiveGoalsAllowed,
    }),
  }));
};

export const defaultCampaignName = () => {
  const timestamp = moment().format('MMMM Do h:mma');
  return `${timestamp} Campaign`;
};
